import {
  getPermissionNamesByIds,
  getRoleNameById,
  getUserToRequest,
} from "../views/dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import { isAdminOrOwner as isAdminOrOwnerRole } from "./organization-permissions-helpers";
import { isPerformActiveCoach, isPullActiveCoach } from "./user-helpers";

/**
 * Returns an object containing various access permissions for a user in an organization.
 *
 * @param {Object} user - The user object.
 * @param {Object} organizationOwner - The organization owner object.
 * @param {Array} globalOrganizationPermissions - The global organization permissions array. Defaults to an empty array.
 * @param {Array} organizationRoles - The organization roles array. Defaults to an empty array.
 * @return {Object} An object containing the following properties:
 *   - userOrganizationCanAccessToWidgets: A boolean indicating if the user can access widgets in the organization.
 *   - userOrganizationCanAccessToExerciseLibrary: A boolean indicating if the user can access the exercise library in the organization.
 *   - userCanAccessToPush: A boolean indicating if the user can access push in the organization.
 *   - userOrganizationCanAccessToTemplates: A boolean indicating if the user can access templates in the organization.
 *   - userCanAccessToPress: A boolean indicating if the user can access press in the organization.
 *   - isAdminOrOwner: A boolean indicating if the user is an admin or owner in the organization.
 *   - isGym: A boolean indicating if the user is a gym in the organization.
 */
export const getUserAccess = (
  user,
  organizationOwner,
  globalOrganizationPermissions = [],
  organizationRoles = []
) => {
  const userIsCoach = user.permissions?.includes("coach");
  const userToRequest = userIsCoach
    ? getUserToRequest(user, organizationOwner)
    : user;

  const { organizationPermissions = [], organizationId } = user;

  const { pushAccountInfo: isFe, pressAccountInfo } = userToRequest;
  const isPerformCoach = isPerformActiveCoach(userToRequest);
  const isPullCoach = isPullActiveCoach(userToRequest);

  //Get the name of the organization's role of the current user. A user with no
  //organizationId is a solo coach (not part of an org), and every access check
  //below already short-circuits on `!organizationId`, so "Owner" here is just a
  //harmless placeholder for them. Only default it when there's no org at all —
  //an org member with a missing organizationRoleId must resolve to no role, not
  //silently escalate to Owner-level product access.
  let roleName = null;
  if (user.organizationRoleId) {
    roleName = getRoleNameById(organizationRoles, user.organizationRoleId);
  } else if (!organizationId) {
    roleName = "Owner";
  }

  //User's permissions (if it's part of an organization, if not, we only are giving a empty array)
  const userOrganizationPermission =
    organizationPermissions.length > 0
      ? getPermissionNamesByIds(
          globalOrganizationPermissions,
          organizationPermissions
        )
      : [""];

  //Constants to detect the user permissions, if the user not belongs to an organization, is able to see the option, and if it's part of an organization
  //We have to check if the user has the permissions
  const userOrganizationCanAccessToWidgets =
    !organizationId ||
    (!!organizationId && userOrganizationPermission.includes("Widgets"));

  const userOrganizationCanAccessToExerciseLibrary =
    !organizationId ||
    (!!organizationId &&
      userOrganizationPermission.includes("Exercise Library"));

  const userOrganizationCanAccessToTemplates =
    !organizationId ||
    (!!organizationId && userOrganizationPermission.includes("Templates"));
  const isAdminOrOwner = isAdminOrOwnerRole(roleName);

  // Product permissions: Owner/Admin get automatic access, others check permissions array
  const userCanAccessToPush =
    (!organizationId && isFe) ||
    (isFe && !!organizationId && isAdminOrOwner) ||
    (isFe && !!organizationId && userOrganizationPermission.includes("PUSH"));
  const userCanAccessToPress =
    (!organizationId && !!pressAccountInfo) ||
    (!!pressAccountInfo && !!organizationId && isAdminOrOwner) ||
    (!!pressAccountInfo &&
      !!organizationId &&
      userOrganizationPermission.includes("PRESS"));
  const userCanAccessToPerform =
    (!organizationId && isPerformCoach) ||
    (isPerformCoach && !!organizationId && isAdminOrOwner) ||
    (isPerformCoach &&
      !!organizationId &&
      userOrganizationPermission.includes("PERFORM"));
  // PULL is the base coaching tier and has no dedicated org permission, so
  // access is limited to active PULL coaches (owner/admin when in an org).
  // `isPullCoach` alone is vacuously true for non-coaches, so gate on userIsCoach.
  const userCanAccessToPull =
    userIsCoach && isPullCoach && (!organizationId || isAdminOrOwner);
  const isGym = !!pressAccountInfo && userCanAccessToPress;

  return {
    userOrganizationCanAccessToWidgets,
    userOrganizationCanAccessToExerciseLibrary,
    userCanAccessToPush,
    userOrganizationCanAccessToTemplates,
    userCanAccessToPress,
    userCanAccessToPerform,
    userCanAccessToPull,
    isAdminOrOwner,
    isGym,
  };
};

/**
 * Resolves scheduler visibility for a coach from their tier access plus each
 * tier's `schedulingEnabled` flag. Shared by the scheduling route guard and the
 * left-nav so both stay in sync.
 *
 * @param {Object} userToRequest - Coach doc holding the *AccountInfo flags.
 * @param {Object} access - Tier booleans from {@link getUserAccess}.
 * @return {{ schedulingEnabled: boolean, hasNonPullScheduling: boolean, hasPressScheduling: boolean, hasPullScheduling: boolean }}
 *   - schedulingEnabled: any tier has scheduling turned on.
 *   - hasNonPullScheduling: PRESS or PERFORM scheduling is turned on. This (not
 *     merely owning the tier) unlocks facilities + per-event access controls, so
 *     a coach who enabled scheduling for PULL only gets neither, even if they
 *     also hold PRESS/PERFORM.
 *   - hasPullScheduling: PULL scheduling is turned on. Used alongside
 *     hasNonPullScheduling to offer a per-event "PULL users" restriction toggle
 *     to coaches who run both PRESS/PERFORM and PULL scheduling.
 */
export const getSchedulingAccess = (
  userToRequest,
  { userCanAccessToPress, userCanAccessToPerform, userCanAccessToPull }
) => {
  const pressSchedulingOn =
    userCanAccessToPress &&
    userToRequest.pressAccountInfo?.schedulingEnabled === true;
  const performSchedulingOn =
    userCanAccessToPerform &&
    userToRequest.performSettings?.schedulingEnabled === true;
  const pullSchedulingOn =
    userCanAccessToPull &&
    userToRequest.pullAccountInfo?.schedulingEnabled === true;

  return {
    schedulingEnabled:
      pressSchedulingOn || performSchedulingOn || pullSchedulingOn,
    hasNonPullScheduling: pressSchedulingOn || performSchedulingOn,
    hasPressScheduling: pressSchedulingOn,
    hasPullScheduling: pullSchedulingOn,
  };
};
